import { Layout } from "../../components/layout/Layout";
import { PageTransition } from "../../components/layout/PageTransition";
import { SEO } from "../../components/SEO";
import Badge from "../../components/ui/Badge";
import { Book, FileText, Code, Video } from "lucide-react";
import styled from "styled-components";

const Section = styled.section`
  padding: 96px 16px;
  position: relative;
  overflow: hidden;

  @media (min-width: ${({ theme }) => theme.breakpoints.sm}px) {
    padding: 96px 24px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}px) {
    padding: 120px 32px;
  }
`;

const BackgroundBlobs = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
`;

const Blob = styled.div`
  position: absolute;
  border-radius: 50%;
  filter: blur(128px);
  animation: pulse-slow 4s ease-in-out infinite;

  &.blob-1 {
    top: 80px;
    right: 25%;
    width: 500px;
    height: 500px;
    background: ${({ theme }) => `${theme.colors.light?.primary || "hsl(258, 90%, 58%)"}20`};
  }

  &.blob-2 {
    bottom: 80px;
    left: 25%;
    width: 400px;
    height: 400px;
    background: ${({ theme }) => `${theme.colors.light?.accent || "hsl(16, 90%, 58%)"}20`};
    animation-delay: 2s;
  }
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  position: relative;
  z-index: 10;
`;

const HeroContent = styled.div`
  max-width: 768px;
  margin: 0 auto;
  text-align: center;
  margin-bottom: 64px;
`;

const BadgeWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 24px;
`;

const Title = styled.h1`
  font-family: ${({ theme }) => theme.typography.fontFamily.display};
  font-size: ${({ theme }) => theme.typography.fontSize.h1};
  font-weight: 700;
  line-height: 1.1;
  margin: 0 0 24px 0;
  color: ${({ theme }) => theme.colors.light?.foreground || theme.colors.text.heading};

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.foreground || "#ffffff"};
  }
`;

const GradientText = styled.span`
  background: ${({ theme }) => theme.colors.gradients.primary};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const Description = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.bodyLarge};
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.light?.muted?.foreground || theme.colors.text.muted};

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.muted?.foreground || "hsl(240, 10%, 60%)"};
  }
`;

const DocsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;

  @media (min-width: ${({ theme }) => theme.breakpoints.md}px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const DocCard = styled.div`
  padding: 32px;
  border-radius: 16px;
  background: ${({ theme }) => theme.colors.light?.card?.background || theme.colors.light?.background || "white"};
  border: 1px solid ${({ theme }) => theme.colors.light?.border || "hsl(240, 10%, 90%)"};
  transition: all 0.5s ease-out;

  body.dark & {
    background: ${({ theme }) => theme.colors.dark?.card?.background || theme.colors.dark?.background || "hsl(240, 15%, 10%)"};
    border-color: ${({ theme }) => theme.colors.dark?.border || "hsl(240, 15%, 18%)"};
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: ${({ theme }) => theme.shadows.md};
  }
`;

const DocIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: ${({ theme }) => theme.colors.gradients.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  color: white;
`;

const DocTitle = styled.h3`
  font-family: ${({ theme }) => theme.typography.fontFamily.display};
  font-weight: 600;
  margin: 0 0 8px 0;
  color: ${({ theme }) => theme.colors.light?.foreground || theme.colors.text.heading};

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.foreground || "#ffffff"};
  }
`;

const DocDescription = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.light?.muted?.foreground || theme.colors.text.muted};
  margin: 0;

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.muted?.foreground || "hsl(240, 10%, 60%)"};
  }
`;

const docs = [
  { icon: Book, title: "Getting Started", description: "Learn the basics and set up your account" },
  { icon: FileText, title: "Guides", description: "Step-by-step tutorials for common tasks" },
  { icon: Code, title: "API Reference", description: "Complete API documentation and examples" },
  { icon: Video, title: "Video Tutorials", description: "Watch video guides and walkthroughs" },
];

export default function Documentation() {
  return (
    <Layout>
      <SEO
        title="Documentation"
        description="Comprehensive documentation to help you get the most out of Kliq CRM."
        keywords="documentation, guides, tutorials, API"
        hreflang
      />
      <PageTransition>
        <Section>
          <BackgroundBlobs>
            <Blob className="blob-1" />
            <Blob className="blob-2" />
          </BackgroundBlobs>
          <Container>
            <HeroContent>
              <BadgeWrapper>
                <Badge $variant="primary">Documentation</Badge>
              </BadgeWrapper>
              <Title>
                Everything you need to <GradientText>get started</GradientText>
              </Title>
              <Description>
                Comprehensive documentation, guides, and tutorials to help you get the most out of Kliq CRM.
              </Description>
            </HeroContent>

            <DocsGrid>
              {docs.map((doc) => (
                <DocCard key={doc.title}>
                  <DocIcon>
                    <doc.icon size={24} />
                  </DocIcon>
                  <DocTitle>{doc.title}</DocTitle>
                  <DocDescription>{doc.description}</DocDescription>
                </DocCard>
              ))}
            </DocsGrid>
          </Container>
        </Section>
      </PageTransition>
    </Layout>
  );
}
